import React, { useState, useEffect, createContext } from "react";
import AdvTaskListingUserDetailDto from "../../../../types/dtos/dashboards/manager/listings/adv-tasks/AdvTaskListingUserDetailDto";
import AdvTaskListingDto from "../../../../types/dtos/dashboards/manager/listings/adv-tasks/AdvTaskListingDto";
import AdvTaskUserListItem from "./AdvTaskUserListItem";
import Accordion from "../../../common/Accordion";
import { ClientTaskType } from "../../../../types/dtos/tasks/advanced-tasks/ClientTaskType";
import ExistingAdvancedTaskPopup from "../../../tasks/advanced-tasks/ExistingAdvancedTaskPopup";
import { ClientTaskTypeCategory } from "../../../../types/dtos/tasks/advanced-tasks/ClientTaskTypeCategory";
import Toast from "../../../common/Toast";
import { advancedTaskHelper } from "../../../../helpers";
import { ToastConfig } from "../../../common/ToastConfig";
import AddAdvancedTaskPopup from "../../../tasks/advanced-tasks/AddAdvancedTaskPopup";
import { BaseUserDetailsDto } from "../../../../types/dtos/generic";
import useTaskManagement from "../../../../hooks/useTaskManagement";

// Create a context to reset pagination across all user items
export const TaskResetPaginationContext = createContext<number>(0);

interface AdvTaskUserViewProps {
  users: AdvTaskListingUserDetailDto[] | null;
  tasks: AdvTaskListingDto[] | null;
  originalTasks?: AdvTaskListingDto[] | null;
  taskType?: ClientTaskType | null;
  onTaskRefreshNeeded: (userId: number) => void;
  expandAll?: boolean;
  onExpandedSectionsChange?: (sections: string[]) => void;
  expandedSections?: string[];
  onClearNotifications?: (userId: number) => void;
  userHasNotifications?: (userId: number) => boolean;
}

const AdvTaskUserView: React.FC<AdvTaskUserViewProps> = ({
  users,
  tasks,
  originalTasks,
  taskType,
  onTaskRefreshNeeded,
  expandAll = false,
  onExpandedSectionsChange,
  expandedSections,
  onClearNotifications,
  userHasNotifications,
}) => {
  const [activeUserSections, setActiveUserSections] = useState<string[]>([]);

  // Counter to trigger pagination resets
  const [resetPaginationCounter, setResetPaginationCounter] = useState(0);

  // Use the task management hook
  const taskManagement = useTaskManagement(
    taskType || null,
    onTaskRefreshNeeded
  );

  // Add Task popup state
  const [isAddTaskPopupOpen, setIsAddTaskPopupOpen] = useState<boolean>(false);
  const [selectedUserForNewTask, setSelectedUserForNewTask] =
    useState<AdvTaskListingUserDetailDto | null>(null);

  // Reset pagination whenever tasks change (which happens when filters are applied)
  useEffect(() => {
    setResetPaginationCounter((prev) => prev + 1);
  }, [tasks]);

  // Handle expandAll changes (expand/collapse all users)
  useEffect(() => {
    const allKeys = getAllUserKeys();
    if (expandAll) {
      setActiveUserSections(allKeys);
    } else if (
      expandAll === false &&
      activeUserSections.length === getAllUserKeys().length
    ) {
      // Only clear sections if expandAll changes from true to false
      setActiveUserSections([]);
    }
  }, [expandAll, users]);

  // Listen for external section changes
  useEffect(() => {
    // If expandedSections is defined and different from our current state, update
    if (
      expandedSections !== undefined &&
      JSON.stringify(expandedSections) !== JSON.stringify(activeUserSections)
    ) {
      setActiveUserSections(expandedSections);
    }
  }, [expandedSections]);

  // Update parent on section changes
  useEffect(() => {
    if (onExpandedSectionsChange) {
      onExpandedSectionsChange(activeUserSections);
    }
  }, [activeUserSections, onExpandedSectionsChange]);

  // Get all accordion item keys for the users
  const getAllUserKeys = () => {
    return users?.map((user) => `user-${user.employeeId}`) || [];
  };

  // Handle adding a new task for a user
  const handleAddTask = (user: AdvTaskListingUserDetailDto) => {
    setSelectedUserForNewTask(user);
    setIsAddTaskPopupOpen(true);
  };

  // Handle task click
  const handleTaskClick = (
    taskId: string,
    categories: ClientTaskTypeCategory[] | null,
    userId: number
  ) => {
    taskManagement.openTaskPopup(taskId, categories, false, userId);
  };

  // Handle notification clearing
  const handleClearNotifications = (userId: number) => {
    if (onClearNotifications) {
      onClearNotifications(userId);
    }
  };

  // Handle add task popup open change
  const handleAddTaskPopupOpenChange = (open: boolean) => {
    setIsAddTaskPopupOpen(open);
    if (!open) {
      setSelectedUserForNewTask(null);
    }
  };

  // Get task type singular name for toast messages
  const getTaskTypeSingularName = (): string => {
    if (taskType && taskType.singularNameTranslationKeyIdentifier) {
      return advancedTaskHelper.ToLowerCase(
        taskType.singularNameTranslationKeyIdentifier
      );
    }
    return "task";
  };

  // Handle successful task creation
  const handleSuccessfulTaskCreation = () => {
    handleAddTaskPopupOpenChange(false);
    taskManagement.setToastConfig(
      ToastConfig.success(
        "Task Created",
        `You have successfully created a new ${getTaskTypeSingularName()}`
      )
    );

    // Refresh the user's tasks
    if (selectedUserForNewTask && onTaskRefreshNeeded) {
      onTaskRefreshNeeded(selectedUserForNewTask.employeeId);
    }
  };

  // Update comment count for a task
  const handleCommentCountChanged = (
    taskId: string,
    newCommentCount: number
  ) => {
    if (!tasks) return;

    // Find the task in the tasks array
    const taskToUpdate = tasks.find((task) => task.userTaskId === taskId);
    if (taskToUpdate) {
      taskToUpdate.commentCount = newCommentCount;
      // Force a re-render
      setResetPaginationCounter((prev) => prev + 1);
    }
  };

  // Create BaseUserDetailsDto for the selected user
  const getUserDetailsForAddTaskPopup = (): BaseUserDetailsDto | null => {
    if (!selectedUserForNewTask) return null;

    return {
      userId: selectedUserForNewTask.employeeId,
      fullName: selectedUserForNewTask.employeeFullName,
      firstName: selectedUserForNewTask.employeeFirstName,
    };
  };

  if (!users || !tasks) {
    return (
      <div className="p-4 text-center text-gray-500">No data available.</div>
    );
  }

  return (
    <TaskResetPaginationContext.Provider value={resetPaginationCounter}>
      <div className="user-view mt-4">
        {/* Toast notification */}
        {taskManagement.toastConfig.show && (
          <Toast
            title={taskManagement.toastConfig.title}
            message={taskManagement.toastConfig.message}
            type={taskManagement.toastConfig.type}
            duration={5000}
            onClose={() => taskManagement.setToastConfig(ToastConfig.hidden())}
          />
        )}

        {users.length === 0 ? (
          <div className="p-4 text-center text-gray-500">No users found.</div>
        ) : (
          <>
            <Accordion
              allowMultipleActiveSections={true}
              activeSection={activeUserSections}
              onActiveSectionChange={(sections) => {
                setActiveUserSections(sections as string[]);
              }}
              allItemKeys={getAllUserKeys()}
            >
              {users.map((user) => (
                <AdvTaskUserListItem
                  key={`user-${user.employeeId}`}
                  user={user}
                  tasks={tasks}
                  originalTasks={originalTasks}
                  taskType={taskType}
                  onTaskClick={(taskId) =>
                    handleTaskClick(
                      taskId,
                      user.activeCategories,
                      user.employeeId
                    )
                  }
                  onClearNotifications={() =>
                    handleClearNotifications(user.employeeId)
                  }
                  hasNotifications={
                    userHasNotifications
                      ? userHasNotifications(user.employeeId)
                      : false
                  }
                  onAddTask={handleAddTask}
                />
              ))}
            </Accordion>

            {/* Pop ups */}
            {taskType && (
              <>
                {/* Add Task popup */}
                {selectedUserForNewTask && (
                  <AddAdvancedTaskPopup
                    usageScenario="DASHBOARD-TASKS"
                    taskType={taskType}
                    activeTasks={tasks.filter(
                      (task) =>
                        task.ownerEmployeeId ===
                          selectedUserForNewTask.employeeId &&
                        (task.overallStatus === "ACTIVE" ||
                          task.overallStatus === "OVERDUE")
                    )}
                    maxOverallTaskRestriction={selectedUserForNewTask.maxTasks}
                    categories={selectedUserForNewTask.activeCategories || []}
                    ownerEmployee={getUserDetailsForAddTaskPopup()!}
                    isOpen={isAddTaskPopupOpen}
                    onOpenChange={handleAddTaskPopupOpenChange}
                    onSuccessfulCreation={handleSuccessfulTaskCreation}
                  />
                )}

                {/* Task popup */}
                {taskManagement.selectedTaskId && (
                  <ExistingAdvancedTaskPopup
                    usageScenario="DASHBOARD-TASKS"
                    taskType={taskType}
                    userTaskId={taskManagement.selectedTaskId}
                    categories={taskManagement.selectedUserCategories || []}
                    isOpen={taskManagement.isExistingTaskPopupOpen}
                    isLocked={taskManagement.isSelectedTaskLocked}
                    onOpenChange={taskManagement.handleTaskPopupOpenChange}
                    onSuccessfulUpdate={taskManagement.handleSuccessfulUpdate}
                    onSuccessfulCancel={taskManagement.handleSuccessfulCancel}
                    onSuccessfulClose={taskManagement.handleSuccessfulClose}
                    onSuccessfulReOpen={taskManagement.handleSuccessfulReOpen}
                    onSuccessfulSavedForLater={
                      taskManagement.handleSuccessfulSavedForLater
                    }
                    onCommentCountChanged={handleCommentCountChanged}
                  />
                )}
              </>
            )}
          </>
        )}
      </div>
    </TaskResetPaginationContext.Provider>
  );
};

export default AdvTaskUserView;
